/**
 * GuidanceBlock — Contextual Operational Guidance V1 (May 25, 2026).
 *
 * Doctrine (operator-defined):
 *   • Top 3-5 items max. Never more.
 *   • Calm. Actionable. No fake urgency.
 *   • "What matters right now" — not a dashboard.
 *   • Each item is fully self-describing: title + why + suggested
 *     action + a single primary CTA + a quiet dismiss.
 *   • Dismissals auto-resurface after the rule's window (default 12h).
 *
 * Renders nothing while loading, nothing on hard error, and a calm
 * "all caught up" pill when there are zero items — never an empty
 * placeholder card that screams for attention.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, AlertTriangle, AlertCircle, Info, ChevronRight, X,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: { icon: AlertTriangle, ring: "ring-rose-300 dark:ring-rose-700",   bar: "bg-rose-500",   chipCls: "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300" },
  high:     { icon: AlertCircle,   ring: "ring-amber-300 dark:ring-amber-700", bar: "bg-amber-500",  chipCls: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
  medium:   { icon: Info,          ring: "ring-blue-300 dark:ring-blue-700",   bar: "bg-blue-500",   chipCls: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  low:      { icon: Info,          ring: "ring-slate-300 dark:ring-slate-700", bar: "bg-slate-400",  chipCls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
};

export default function GuidanceBlock({ role = null, compact = false }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState({});  // dedup_key → true while dismissing

  const load = useCallback(async () => {
    try {
      const params = role ? `?role=${encodeURIComponent(role)}` : "";
      const r = await axios.get(`${API}/api/guidance/today${params}`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch {
      // Silent failure — guidance never breaks the page.
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (it) => {
    setBusy((b) => ({ ...b, [it.dedup_key]: true }));
    try {
      await axios.post(`${API}/api/guidance/dismiss`,
        { dedup_key: it.dedup_key, hours: it.dismiss_window_hours || 12 },
        { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.dedup_key !== it.dedup_key));
      toast.success(`Hidden for ${it.dismiss_window_hours || 12} hours`);
    } catch {
      toast.error("Couldn't dismiss");
    } finally {
      setBusy((b) => ({ ...b, [it.dedup_key]: false }));
    }
  };

  // Render nothing while we don't yet know if there's anything.
  if (!loaded) return null;

  // Empty state — calm, one-line, easy to ignore.
  if (items.length === 0) {
    return (
      <div
        data-testid="guidance-block-empty"
        className="rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/50 dark:bg-emerald-900/20 px-4 py-2.5 flex items-center gap-2 text-emerald-700 dark:text-emerald-300"
      >
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        <p className="text-[12.5px] font-bold">You're caught up. Nothing needs your attention right now.</p>
      </div>
    );
  }

  return (
    <section
      data-testid="guidance-block"
      aria-label="What matters right now"
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
    >
      <header className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-slate-100 dark:border-slate-800">
        <Sparkles className="w-3.5 h-3.5 text-slate-400" />
        <p className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500">
          What matters right now
        </p>
        <span className="ml-auto text-[10px] text-slate-400 font-mono">
          {items.length} item{items.length === 1 ? "" : "s"}
        </span>
      </header>
      <ul className={compact ? "divide-y divide-slate-100 dark:divide-slate-800" : "divide-y divide-slate-100 dark:divide-slate-800"}>
        {items.map((it) => {
          const sev = SEVERITY[it.severity] || SEVERITY.low;
          const Icon = sev.icon;
          return (
            <li
              key={it.dedup_key}
              data-testid={`guidance-item-${it.dedup_key}`}
              className="relative px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${sev.bar}`} />
              <div className="flex items-start gap-3 ml-1">
                <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center ${sev.chipCls}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white leading-tight">
                    {it.title}
                  </p>
                  {!compact && it.why && (
                    <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                      {it.why}
                    </p>
                  )}
                  {it.suggested_action && (
                    <p className="text-[11.5px] text-slate-600 dark:text-slate-300 mt-1.5 leading-snug">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mr-1">Do:</span>
                      {it.suggested_action}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    {it.action_route && (
                      <Link
                        to={it.action_route}
                        data-testid={`guidance-action-${it.dedup_key}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[11px] font-bold hover:bg-slate-700 dark:hover:bg-slate-100"
                      >
                        Open
                        <ChevronRight className="w-3 h-3" />
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => dismiss(it)}
                      disabled={busy[it.dedup_key]}
                      data-testid={`guidance-dismiss-${it.dedup_key}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-40"
                      title={`Hide for ${it.dismiss_window_hours || 12} hours`}
                    >
                      <X className="w-3 h-3" />
                      <span>Hide for {it.dismiss_window_hours || 12}h</span>
                    </button>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
